import { RosterImportPreview } from "./roster-import-preview.js";
import { useState } from "react";
import type {
  OpenProjectRosterPerson,
  OpenProjectRosterPreview,
} from "@payday/contracts";
import { api, type Session } from "../api.js";
import { failure, Notice, useData } from "../shared/ui.js";
import { Button } from "../payroll/components/ui/Button.js";

function readWorkbook(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.name.toLowerCase().endsWith(".xlsx") || file.size > 2_000_000) {
      reject(new Error("Choose the exported XLSX file, smaller than 2 MB."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error("The selected file could not be read."));
    reader.onload = () =>
      resolve(
        typeof reader.result === "string"
          ? (reader.result.split(",")[1] ?? "")
          : "",
      );
    reader.readAsDataURL(file);
  });
}
export function OpenProjectRoster({ session }: { session: Session }) {
  const status = useData(() => api.rosterStatus(), []);
  const [preview, setPreview] = useState<OpenProjectRosterPreview | null>(null);
  const [workbook, setWorkbook] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<
    readonly OpenProjectRosterPerson[] | null
  >(null);
  const [selected, setSelected] = useState("");
  const revision = session.principal.authorizationRevision;
  async function action(task: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await task();
    } catch (error) {
      setError(error instanceof Error ? error.message : failure(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="control-card" aria-labelledby="roster-title">
      <div className="section-heading">
        <div>
          <h3 id="roster-title">People in PayDay</h3>
          <p>
            Review the roster once in Excel. Normal sync updates approved
            people; add new starters explicitly.
          </p>
        </div>
        <span>
          {status.value
            ? `${status.value.included} people on roster`
            : "Loading roster�"}
        </span>
      </div>
      {status.error !== undefined && (
        <Notice error>
          {failure(status.error)}{" "}
          <Button variant="outline" onClick={status.refresh}>
            Retry roster
          </Button>
        </Notice>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() =>
            void action(async () => {
              const file = await api.exportRoster();
              const url = URL.createObjectURL(file.blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = file.name;
              anchor.click();
              URL.revokeObjectURL(url);
            })
          }
        >
          Export people to Excel
        </Button>
        <label className="flex flex-col gap-1">
          <span>Reviewed Excel file</span>
          <input
            type="file"
            accept=".xlsx"
            disabled={busy}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              setPreview(null);
              setWorkbook("");
              if (file)
                void action(async () => {
                  const bytes = await readWorkbook(file);
                  const review = await api.previewRoster(bytes);
                  setWorkbook(bytes);
                  setPreview(review);
                });
            }}
          />
        </label>
        <Button
          variant="outline"
          disabled={busy || !status.value}
          onClick={() =>
            void action(async () => {
              setCandidates((await api.rosterCandidates()).people);
            })
          }
        >
          Add person
        </Button>
      </div>
      {preview && (
        <RosterImportPreview
          preview={preview}
          busy={busy}
          onApply={() =>
            void action(async () => {
              const result = await api.applyRoster(
                workbook,
                preview.checksum,
                revision,
              );
              setPreview(null);
              setWorkbook("");
              status.refresh();
              setNotice(
                `Roster saved: ${result.people.length} approved people.`,
              );
            })
          }
        />
      )}
      {candidates && (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span>OpenProject person</span>
            <select
              value={selected}
              disabled={busy}
              onChange={(event) => setSelected(event.target.value)}
            >
              <option value="">Choose a person</option>
              {candidates.map((p) => (
                <option key={p.openProjectUserId} value={p.openProjectUserId}>
                  {p.displayName} ({p.username}){p.active ? "" : " � inactive"}
                </option>
              ))}
            </select>
          </label>
          <Button
            disabled={busy || !selected || !status.value}
            onClick={() =>
              void action(async () => {
                if (!status.value) return;
                await api.addRosterPerson(
                  Number(selected),
                  status.value.checksum,
                  revision,
                );
                status.refresh();
                setCandidates(null);
                setSelected("");
                setNotice(
                  "Person added. Set their contract terms in Work Profiles.",
                );
              })
            }
          >
            Add selected person
          </Button>
        </div>
      )}
      {busy && <Notice>Working�</Notice>}
      {error && <Notice error>{error}</Notice>}
      {notice && <Notice>{notice}</Notice>}
    </section>
  );
}
